import React, { useEffect, useState } from "react";
import ProfileCard from "../../common/profileCard";
import { useSelector } from "react-redux";
import apiInstance from "../../../../utility/apiInstance";
import RecruiterSidebar from "../../common/recruiterSidebar";
import SettingsLinks from "../settingsLinks/settingsLinks";

interface Notification {
    id: string;
    type: string;
    title: string;
    message: string;
    read: boolean;
    createdAt: string;
}

const typeIcon: Record<string, string> = {
    application_status_changed: "isax isax-document-text",
    application_submitted: "isax isax-send-2",
    new_program_added: "isax isax-book-1",
    recruiter_status_updated: "isax isax-verify",
};

const typeColor: Record<string, string> = {
    application_status_changed: "bg-info-transparent",
    application_submitted: "bg-primary-transparent",
    new_program_added: "bg-success-transparent",
    recruiter_status_updated: "bg-warning-transparent",
};

const formatRelativeTime = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min${mins > 1 ? "s" : ""} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
    return new Date(dateStr).toLocaleDateString();
};

const isToday = (dateStr: string): boolean => {
    const d = new Date(dateStr);
    const now = new Date();
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const isYesterday = (dateStr: string): boolean => {
    const d = new Date(dateStr);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth() && d.getFullYear() === yesterday.getFullYear();
};

const RecruiterNotifications = () => {
    const user = useSelector((state: any) => state.auth.user);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [actioning, setActioning] = useState(false);
    const [settingsRefreshKey, setSettingsRefreshKey] = useState(0);

    const refreshSettingsBadge = () => setSettingsRefreshKey((k) => k + 1);

    const fetchNotifications = async () => {
        try {
            const res = await apiInstance.get("/api/notifications", {
                params: {
                    "where[recipient][equals]": user.id,
                    sort: "-createdAt",
                    limit: 500,
                },
            });
            setNotifications(res.data.docs);
        } catch (err) {
            console.error("Failed to fetch notifications:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id) fetchNotifications();
    }, [user?.id]);

    const markAsRead = async (id: string) => {
        try {
            await apiInstance.patch(`/api/notifications/${id}`, { read: true });
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true } : n))
            );
            refreshSettingsBadge();
        } catch (err) {
            console.error("Failed to mark notification as read:", err);
        }
    };

    const markAllAsRead = async () => {
        if (actioning) return;
        setActioning(true);
        try {
            await apiInstance.post("/api/recruiter/notifications/mark-all-read");
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            refreshSettingsBadge();
        } catch (err) {
            console.error("Failed to mark all as read:", err);
        } finally {
            setActioning(false);
        }
    };

    const deleteNotification = async (id: string) => {
        try {
            await apiInstance.delete(`/api/notifications/${id}`);
            setNotifications((prev) => prev.filter((n) => n.id !== id));
            refreshSettingsBadge();
        } catch (err) {
            console.error("Failed to delete notification:", err);
        }
    };

    const deleteAllNotifications = async () => {
        if (actioning) return;
        setActioning(true);
        try {
            const results = await Promise.allSettled(
                notifications.map((n) => apiInstance.delete(`/api/notifications/${n.id}`))
            );
            const deletedIds = new Set(
                notifications
                    .filter((_, i) => results[i].status === "fulfilled")
                    .map((n) => n.id)
            );
            setNotifications((prev) => prev.filter((n) => !deletedIds.has(n.id)));
            refreshSettingsBadge();
        } catch (err) {
            console.error("Failed to delete all notifications:", err);
        } finally {
            setActioning(false);
        }
    };

    const todayItems = notifications.filter((n) => isToday(n.createdAt));
    const yesterdayItems = notifications.filter((n) => isYesterday(n.createdAt));
    const earlierItems = notifications.filter((n) => !isToday(n.createdAt) && !isYesterday(n.createdAt));
    const unreadCount = notifications.filter((n) => !n.read).length;

    const renderItem = (item: Notification) => (
        <div
            key={item.id}
            className={`notification-item mb-3 ${!item.read ? "border-start border-primary border-3" : ""}`}
            style={{ cursor: item.read ? "default" : "pointer" }}
            onClick={() => { if (!item.read) markAsRead(item.id); }}
        >
            <div className="row align-items-center row-gap-2">
                <div className="col-md-10">
                    <div className="d-flex align-items-center">
                        <span className={`icon-box ${typeColor[item.type] || "bg-primary-transparent"} me-3 flex-shrink-0`} style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className={typeIcon[item.type] || "isax isax-notification-bing"} />
                        </span>
                        <div>
                            <p className={`mb-0 fs-15 ${!item.read ? "fw-semibold" : ""}`}>
                                {item.title}
                            </p>
                            <p className="text-gray-5 fs-14 mb-0">{item.message}</p>
                            <p className="text-muted fs-13 mb-0">{formatRelativeTime(item.createdAt)}</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-2 d-flex align-items-center justify-content-end gap-2">
                    {!item.read && (
                        <span className="badge bg-primary rounded-pill" style={{ width: 8, height: 8, padding: 0 }} />
                    )}
                    {!item.read && (
                        <button
                            className="btn btn-sm btn-light rounded-pill"
                            onClick={(e) => { e.stopPropagation(); markAsRead(item.id); }}
                            title="Mark as read"
                        >
                            <i className="isax isax-tick-circle fs-14" />
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-light rounded-pill"
                        onClick={(e) => { e.stopPropagation(); deleteNotification(item.id); }}
                        title="Delete"
                    >
                        <i className="isax isax-trash fs-14" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="content">
                <div className="container">
                    <ProfileCard />
                    <div className="row">
                        <RecruiterSidebar />
                        <div className="col-lg-9">
                            <div className="mb-3">
                                <h5>Settings</h5>
                            </div>
                            <SettingsLinks refreshKey={settingsRefreshKey} />
                            <div className="mt-4">
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div>
                                        <h6 className="fs-18 fw-semibold mb-0">
                                            Notifications
                                            {unreadCount > 0 && (
                                                <span className="badge bg-primary ms-2">{unreadCount}</span>
                                            )}
                                        </h6>
                                    </div>
                                    {notifications.length > 0 && (
                                        <div className="d-flex gap-2">
                                            {unreadCount > 0 && (
                                                <button
                                                    className="btn btn-light rounded-pill btn-sm"
                                                    onClick={markAllAsRead}
                                                    disabled={actioning}
                                                >
                                                    <i className="isax isax-tick-square me-1" />
                                                    Mark All as Read
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-secondary rounded-pill btn-sm"
                                                onClick={deleteAllNotifications}
                                                disabled={actioning}
                                            >
                                                <i className="isax isax-trash me-1" />
                                                Delete All
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {loading ? (
                                    <p className="text-muted">Loading notifications...</p>
                                ) : notifications.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="isax isax-notification-bing fs-48 text-muted" />
                                        <p className="text-muted mt-3">No notifications yet</p>
                                    </div>
                                ) : (
                                    <>
                                        {todayItems.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="fs-14 text-muted fw-semibold mb-3 text-uppercase">Today</h6>
                                                {todayItems.map(renderItem)}
                                            </div>
                                        )}
                                        {yesterdayItems.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="fs-14 text-muted fw-semibold mb-3 text-uppercase">Yesterday</h6>
                                                {yesterdayItems.map(renderItem)}
                                            </div>
                                        )}
                                        {earlierItems.length > 0 && (
                                            <div className="mb-4">
                                                <h6 className="fs-14 text-muted fw-semibold mb-3 text-uppercase">Earlier</h6>
                                                {earlierItems.map(renderItem)}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RecruiterNotifications;
